import {
  BackendFlightDirection,
  FlightBadgeKind,
  FlightCardResponse,
  RecordPlateResponse,
} from "../../../api/flightAlerts.service";
import { FlightDirection, FlightFixture, FlightRecord, FlightSignal } from "../types";
import { timeToMinutes } from "./time.helper";

export const toBackendDirection = (direction: FlightDirection): BackendFlightDirection =>
  direction === "arrival" ? "ARRIVAL" : "DEPARTURE";

const toDirection = (direction: BackendFlightDirection): FlightDirection =>
  direction === "ARRIVAL" ? "arrival" : "departure";

// Backend "local" wall-clock strings are "YYYY-MM-DD HH:MM" (dash-data's HKT local format) —
// take just the trailing HH:MM, defensively falling back to the whole string if it's ever
// passed bare.
const extractTimeOfDay = (local: string): string => {
  const parts = local.trim().split(/\s+/);
  return parts[parts.length - 1] ?? local;
};

const toMinutes = (local: string): number => timeToMinutes(extractTimeOfDay(local));

/**
 * Reads an already-present `estimated.local` as minutes, tolerating a backend regression where
 * `local` isn't a real HKT wall-clock string (e.g. the raw UTC ISO value echoed into it —
 * DASH-6077's "NaN:NaN" card bug). Rather than surface NaN, derive the same instant from the
 * schedule plus the badge's own delay, which is what a correctly-formatted `local` would have
 * given anyway. Does not invent an estimate when the backend sent none — only guards a malformed
 * one that's already present.
 */
const parseEstimatedMinutes = (
  local: string,
  scheduledMinutes: number,
  badgeDelayMinutes: number | null,
): number => {
  const minutes = toMinutes(local);
  if (!Number.isNaN(minutes)) return minutes;
  return badgeDelayMinutes !== null ? scheduledMinutes + badgeDelayMinutes : scheduledMinutes;
};

const toShortDate = (isoDate: string): string =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });

/**
 * Reconstructs a FlightSignal from the backend's already-precedence-resolved `badge`, so
 * signal-driven rendering (hall/gate sections etc.) keeps working. `STATUS_UNAVAILABLE` maps
 * straight to `{ kind: "unknown" }` — it must never fall through to the estimated/scheduledOnly
 * default below, which would fold a no-estimate leg into "on time".
 */
const toSignal = (card: FlightCardResponse): FlightSignal => {
  if (card.cancelled) return { kind: "cancelled" };
  switch (card.badge) {
    case FlightBadgeKind.LANDED:
      return { kind: "landed" };
    case FlightBadgeKind.DEPARTED:
      return { kind: "departed" };
    case FlightBadgeKind.GATE_CLOSED:
      return { kind: "gateClosed" };
    case FlightBadgeKind.FINAL_CALL:
      return { kind: "finalCall" };
    case FlightBadgeKind.BOARDING:
      return { kind: "boarding" };
    case FlightBadgeKind.STATUS_UNAVAILABLE:
      return { kind: "unknown" };
    default:
      return card.estimated ? { kind: "estimated" } : { kind: "scheduledOnly" };
  }
};

export const mapFlightCardResponse = (
  card: FlightCardResponse,
  isoDate: string,
): Omit<FlightFixture, "record"> => {
  const isResolved = card.badge === FlightBadgeKind.LANDED || card.badge === FlightBadgeKind.DEPARTED;
  const scheduledMinutes = toMinutes(card.scheduled.local);
  const estimatedMinutes = card.estimated
    ? parseEstimatedMinutes(card.estimated.local, scheduledMinutes, card.badgeDelayMinutes)
    : null;

  return {

    id: `${card.designator}-${isoDate}-${card.direction.toLowerCase()}-${card.scheduled.utc}`,
    flightNumber: card.designator,
    direction: toDirection(card.direction),

    otherAirport: card.otherAirport,
    isoDate,
    dateDisplay: toShortDate(isoDate),

    ...(card.hall ? { hall: card.hall } : {}),
    ...(card.terminal ? { terminal: card.terminal } : {}),
    ...(card.gate ? { gate: card.gate } : {}),
    ...(card.gateClosingTime ? { gateClosesAtMinutes: toMinutes(card.gateClosingTime) } : {}),
    ...(card.arrivalFieldsUnavailableNote ? { arrivalFieldsUnavailableNote: true as const } : {}),
    scheduledMinutes,
    ...(estimatedMinutes !== null ? { estimatedMinutes } : {}),
    ...(isResolved ? { resolvedTimeMinutes: estimatedMinutes ?? scheduledMinutes } : {}),
    lastUpdated: card.lastUpdated,
    badge: card.badge,
    badgeDelayMinutes: card.badgeDelayMinutes,
    signal: toSignal(card),

    feedUnavailable: card.stale,
  };
};

export const mapRecordPlateResponse = (plate: RecordPlateResponse): FlightRecord => ({
  sampleSize: plate.sampleSize,
  windowDays: plate.windowDays,
  medianMinutes: plate.medianDelayMinutes ?? 0,
  bands: [
    { key: "early", count: plate.bands.early.count },
    { key: "onTime", count: plate.bands.onTime.count },
    { key: "late", count: plate.bands.late.count },
  ],
  earlyOrOnTimePct: plate.onTimeOrEarlyPct,
  worstDelayMinutes: plate.worstDelay?.minutes ?? 0,
  worstDelayDate: plate.worstDelay ? toShortDate(plate.worstDelay.date) : "—",
  cancelledCount: plate.cancelledCount,
});

export const EMPTY_RECORD: FlightRecord = {
  sampleSize: 0,
  windowDays: 90,
  medianMinutes: 0,
  bands: [
    { key: "early", count: 0 },
    { key: "onTime", count: 0 },
    { key: "late", count: 0 },
  ],
  earlyOrOnTimePct: 0,
  worstDelayMinutes: 0,
  worstDelayDate: "—",
  cancelledCount: 0,
};
